import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MapPin, ChevronRight, ArrowLeft, Shield, Star, Phone } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { CitySearch } from '../components/CitySearch';
import { USAMap } from '../components/USAMap';
import toast from 'react-hot-toast'; 

interface State {
  id: string;
  code: string;
  name: string;
  cities: City[];
}

interface City {
  id: string;
  name: string;
  slug: string;
  zip_codes: string[];
}

export function LocationsPage() {
  const [states, setStates] = useState<State[]>([]);
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    async function loadStates() {
      try {
        // First get all states
        const { data: statesData, error: statesError } = await supabase
          .from('states')
          .select('id, code, name')
          .order('name');

        if (statesError) throw statesError;

        // Then get cities for each state
        const statesWithCities = await Promise.all(
          (statesData || []).map(async (state) => {
            const { data: cities, error: citiesError } = await supabase
              .from('cities')
              .select('id, name, slug, zip_codes')
              .eq('state_id', state.id);

            if (citiesError) throw citiesError;

            return {
              ...state,
              cities: cities || []
            };
          })
        );

        setStates(statesWithCities);
      } catch (error) {
        console.error('Error loading states:', error);
        setError('Failed to load states');
        toast.error('Failed to load states');
      } finally {
        setLoading(false);
      }
    }

    loadStates();
  }, []);

  const handleStateClick = (stateCode: string) => {
    setSelectedState(stateCode === selectedState ? null : stateCode);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Service Areas | Find Local Licensed Plumbers Near You - Plumber.Guru</title>
        <meta name="description" content="Find trusted local plumbers in your area. Professional plumbing services available 24/7 across major US cities. Licensed & insured plumbers, upfront pricing." />
        <meta name="keywords" content="plumber near me, local plumber, emergency plumber, plumbing service areas, plumbing services, licensed plumber" />
        <link rel="canonical" href="https://plumbers.guru/locations" />
      </Helmet>

      {loading ? (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-xl text-gray-600">Loading service areas...</div>
        </div>
      ) : error ? (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-xl text-red-600">{error}</div>
        </div>
      ) : states.length === 0 ? (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-xl text-gray-600">No service areas available yet</div>
        </div>
      ) : (
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-blue-600 to-blue-800 py-16">
          <div className="absolute inset-0">
            <img
              className="w-full h-full object-cover opacity-20"
              src="https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?auto=format&fit=crop&q=80"
              alt="Plumbing background"
            />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-white mb-4">
                Find Plumbers in Your Area
              </h1>
              <p className="text-xl text-gray-100 max-w-3xl mx-auto mb-8">
                24/7 Emergency Service • Licensed & Insured • Fair Pricing
              </p>
              <div className="max-w-xl mx-auto">
                <CitySearch />
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
              Service Areas by State
            </h2>
            <p className="text-xl text-gray-600 text-center max-w-3xl mx-auto">
              Find professional plumbing services in your state
            </p>
          </div>

          {/* USA Map */}
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <USAMap
              states={states.map(state => ({
                id: state.code,
                name: state.name,
                citiesCount: state.cities.length
              }))}
              selectedState={selectedState || undefined}
              onStateClick={handleStateClick}
            />
            <div className="mt-4 text-center text-sm text-gray-600">
              Click on a state to view available service areas
            </div>
          </div>

          {/* State Cities List */}
          {selectedState && (
            <div className="bg-white rounded-lg shadow-lg p-6 mt-8">
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={() => setSelectedState(null)}
                  className="flex items-center text-blue-600 hover:text-blue-700"
                >
                  <ArrowLeft className="w-5 h-5 mr-1" />
                  Back to Map
                </button>
                <h3 className="text-xl font-bold text-gray-900">
                  {states.find(s => s.code === selectedState)?.name}
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {states
                  .find(s => s.code === selectedState)
                  ?.cities.map(city => (
                    <div
                      key={city.id}
                      className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden"
                    >
                      <div 
                        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => {
                          const elem = document.getElementById(`zip-codes-${city.id}`);
                          if (elem) {
                            elem.style.display = elem.style.display === 'none' ? 'block' : 'none';
                          }
                        }}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className="font-medium text-gray-900">{city.name}</h4>
                            <span className="text-sm text-gray-600">
                              {city.zip_codes.length} service areas
                            </span>
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </div>
                      </div>
                      <div 
                        id={`zip-codes-${city.id}`} 
                        className="hidden border-t border-gray-200"
                      >
                        <div className="p-4 bg-gray-50">
                          <h5 className="text-sm font-medium text-gray-700 mb-2">Service Areas</h5>
                          <div className="grid grid-cols-2 gap-2">
                            {city.zip_codes.map(zip => (
                              <Link
                                key={zip}
                                to={`/${selectedState.toLowerCase()}/${city.slug}/${zip}`}
                                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {zip}
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Popular Cities */}
          {!selectedState && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Link 
                to="/il/chicago/60601" 
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow group"
              >
                <MapPin className="w-8 h-8 text-blue-600 mb-3 group-hover:text-blue-700" />
                <h3 className="font-semibold group-hover:text-blue-600">Chicago, IL</h3>
              </Link>
              <Link 
                to="/tx/dallas/75201" 
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow group"
              >
                <MapPin className="w-8 h-8 text-blue-600 mb-3 group-hover:text-blue-700" />
                <h3 className="font-semibold group-hover:text-blue-600">Dallas, TX</h3>
              </Link>
              <Link 
                to="/ca/los-angeles/90001" 
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow group"
              >
                <MapPin className="w-8 h-8 text-blue-600 mb-3 group-hover:text-blue-700" />
                <h3 className="font-semibold group-hover:text-blue-600">Los Angeles, CA</h3>
              </Link>
              <Link 
                to="/fl/miami/33101" 
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow group"
              >
                <MapPin className="w-8 h-8 text-blue-600 mb-3 group-hover:text-blue-700" />
                <h3 className="font-semibold group-hover:text-blue-600">Miami, FL</h3>
              </Link>
            </div>
          )}
        </div>

        {/* Benefits Section */}
        <div className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Why Choose Our Plumbers
              </h2>
              <p className="text-xl text-gray-600">
                Professional service you can trust
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-6">
                <Phone className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">24/7 Availability</h3>
                <p className="text-gray-600">
                  Emergency service available around the clock
                </p>
              </div>
              <div className="text-center p-6">
                <Shield className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Licensed & Insured</h3>
                <p className="text-gray-600">
                  All plumbers are fully licensed and insured
                </p>
              </div>
              <div className="text-center p-6">
                <Star className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Satisfaction Guaranteed</h3>
                <p className="text-gray-600">
                  100% satisfaction on all services
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}
    </>
  );
}